// Complaints.jsx

import React, { useState, useEffect, useContext, Suspense } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Tab,
  Snackbar,
  Pagination,
  Autocomplete,
  TextField,
  Modal,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { useNavigate } from 'react-router-dom';
import { RoleContext } from 'src/context/roleContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import { SKOLA } from 'src/constants/role';
import {
  FLAG_FINALE,
  FLAG_ZALBE,
  modeDescriptions
} from 'src/constants/competitions';
import { ImageUrl } from 'src/constants/imagesConst';
import Header from 'src/components/Header';
import { useTheme } from '@mui/material/styles';
import { API_BASE_URL } from 'src/apiConfig';
import EditComplaintDialog from './editComplaint';
import { classes } from 'src/constants/general';
import {
  StyledTabs,
  TabsWrapper
} from 'src/components/styledHelpComponents/styledHelpComponents';
import { cyrillicToLatin, latinToCyrillic } from 'src/helper/helperFunction';
import { StyledSearch } from './administration/helper';

/* SKOLA STRANA ZA PRIKAZ SVOJIH ZALBI */

const Complaints = () => {
  const { activeModes } = useContext(ActiveModesContext);
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [tabIndex, setTabIndex] = useState(0);
  const itemsPerPage = 12;
  const theme = useTheme();
  const { t } = useTranslation();
  const { role, schoolId } = useContext(RoleContext);
  const [disabledCrudComplaint, setDisabledCrudComplaint] = useState('');
  const [disabledViewComplaint, setDisabledViewComplaint] = useState('');
  const [classFilter, setClassFilter] = useState(null);
  const [complaintThread, setComplaintThread] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchComplaints = async () => {
    try {
      let url = `${API_BASE_URL}/api/complaints.php`;

      if (role === SKOLA) {
        url += `?schoolId=${schoolId}&grouped=true`;
      }

      const response = await axios.get(url);
      setComplaints(
        response.data.map((complaint) => ({
          ...complaint,
          date: new Date(
            parseInt(complaint.last_date) * 1000
          ).toLocaleDateString(),
          student: {
            name: complaint.student_name,
            class: complaint.class,
            professor: complaint.professor
          }
        }))
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    }
  };

  useEffect(() => {
    setDisabledCrudComplaint(!activeModes.includes(FLAG_ZALBE));
    setDisabledViewComplaint(activeModes.includes(FLAG_FINALE));
    fetchComplaints();
  }, [role, schoolId, activeModes]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/complaints.php?id=${id}`);
      setComplaints(complaints.filter((complaint) => complaint.id !== id));
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete complaint:', error);
    }
  };

  const handleEdit = (complaint) => {
    if (!disabledCrudComplaint) {
      setSelectedComplaint(complaint);
      setEditMode(true);
    }
  };

  const handleCloseEditDialog = () => {
    setSelectedComplaint(null);
    setEditMode(false);
  };

  const handleOpenDeleteDialog = (complaint) => {
    if (!disabledCrudComplaint) {
      setSelectedComplaint(complaint);
      setDeleteDialogOpen(true);
    }
  };

  const handleCloseDeleteDialog = () => {
    setSelectedComplaint(null);
    setDeleteDialogOpen(false);
  };

  const handleView = async (complaint) => {
    const response = await axios.get(`${API_BASE_URL}/api/complaints.php`, {
      params: {
        thread: true,
        id_skole: complaint.id_skole,
        id_ucenika: complaint.id_ucenika
      }
    });
    setComplaintThread(response.data);

    setSelectedComplaint(complaint);
    setViewMode(true);

    // Scroll to bottom when messages are loaded
    setTimeout(() => {
      const messagesContainer = document.getElementById('messagesContainer');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  };

  const handleCloseViewDialog = () => {
    setSelectedComplaint(null);
    setViewMode(false);
    setNewMessage('');
    setComplaintThread([]);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleClassFilterChange = (event, value) => {
    setClassFilter(value);
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchQuery(searchValue);
  };

  const decodeHtmlEntities = (text) => {
    const element = document.createElement('div');
    if (text) {
      element.innerHTML = text;
      return element.textContent;
    }
    return text;
  };

  const handleSendMessage = async () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const messageData = {
      id_skole: selectedComplaint.id_skole,
      id_ucenika: selectedComplaint.id_ucenika,
      poruku_poslao: 'skola',
      poruka: newMessage,
      status: 0,
      skola_procitao: 1,
      admin_procitao: 0,
      date: timestamp
    };

    await axios.post(`${API_BASE_URL}/api/complaints.php`, messageData);

    setComplaintThread((prevThread) => [...prevThread, messageData]);

    setNewMessage('');

    // Scroll to bottom after sending a new message
    setTimeout(() => {
      const messagesContainer = document.getElementById('messagesContainer');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  };

  // Filter complaints based on selected tab and search
  const filterComplaints = () => {
    let filtered = complaints;
    if (tabIndex === 0) {
      filtered = complaints;
    } else if (tabIndex === 1) {
      filtered = filtered.filter((c) => c.admin_procitao === '0');
    } else if (tabIndex === 2) {
      filtered = filtered.filter((c) => c.admin_procitao === '1');
    }
    if (classFilter) {
      filtered = filtered.filter(
        (c) => parseInt(c.student.class) === classFilter
      );
    }
    if (searchQuery) {
      const searchLatin = cyrillicToLatin(searchQuery);
      const searchCyrillic = latinToCyrillic(searchQuery);

      filtered = filtered.filter((complaint) => {
        return (
          (complaint.student.name &&
            (cyrillicToLatin(complaint.student.name.toLowerCase()).includes(
              searchLatin
            ) ||
              latinToCyrillic(complaint.student.name.toLowerCase()).includes(
                searchCyrillic
              ))) ||
          (complaint.student.professor &&
            (cyrillicToLatin(
              complaint.student.professor.toLowerCase()
            ).includes(searchLatin) ||
              latinToCyrillic(
                complaint.student.professor.toLowerCase()
              ).includes(searchCyrillic)))
        );
      });
    }
    return filtered;
  };

  const displayedComplaints = filterComplaints().slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (!activeModes.includes(FLAG_ZALBE) && !activeModes.includes(FLAG_FINALE)) {
    return (
      <Box
        display="grid"
        gap={3}
        p={2}
        sx={{
          backgroundColor: '#e0f7fa',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {t('complaints.noCreateComplaints')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title={t('meni.complaints')} />

      <TabsWrapper>
        <StyledTabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label={t('complaints.tabsLabel')}
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}
        >
          <Tab label={t('complaints.all')} />
          <Tab label={t('complaints.pending')} />
          <Tab label={t('complaints.responded')} />
        </StyledTabs>
      </TabsWrapper>

      <Box my={3} display="flex" gap={2}>
        <StyledSearch
          label={t('search.placeholder')}
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
        />

        <Autocomplete
          options={classes}
          value={classFilter}
          onChange={handleClassFilterChange}
          getOptionLabel={(option) => `${option}. ${t('results.grade1')}`}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('columns.selectClass')}
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px'
              }}
            />
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          sx={{ width: '250px' }}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
        gap={3}
        p={2}
      >
        {displayedComplaints.map((complaint, index) => (
          <Card
            key={`${complaint.id}-${index}`}
            sx={{
              backgroundColor:
                complaint.admin_procitao === '1' ? '#e0f7fa' : '#ffffff',
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '16px',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                cursor: 'pointer'
              }
            }}
            onClick={() => handleView(complaint)}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}
              >
                <Box
                  component="img"
                  src={ImageUrl.complaint}
                  alt="Complaint Illustration"
                  sx={{ width: '60px', height: '60px', marginRight: '16px' }}
                />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="#1d7998">
                    {complaint.student ? complaint.student.name : ''}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {t('complaints.professor')}:
                    {complaint.student ? complaint.student.professor : ''}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: 'italic', color: theme.palette.text.primary }}
              >
                {t('complaints.clickToView')}
              </Typography>

              <Box display="flex" alignItems="center" mt={2}>
                <AccessTimeIcon
                  sx={{
                    verticalAlign: 'middle',
                    color: theme.palette.primary.main,
                    mr: 1
                  }}
                />
                <Typography variant="body2" color="textSecondary">
                  {complaint.date}
                </Typography>

                <Typography variant="body2" color="textSecondary" ml={2}>
                  {complaint.admin_procitao === '1' ? (
                    <VisibilityIcon
                      sx={{ verticalAlign: 'middle', color: '#4caf50', mr: 1 }}
                    />
                  ) : (
                    <VisibilityOffIcon
                      sx={{ verticalAlign: 'middle', color: '#ff6f61', mr: 1 }}
                    />
                  )}
                  {t(
                    complaint.admin_procitao === '1'
                      ? 'dialog.read'
                      : 'dialog.unread'
                  )}
                </Typography>
              </Box>
            </CardContent>

            {!(tabIndex === 2 || complaint.admin_procitao === '1') && (
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<EditRoundedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(complaint);
                  }}
                  sx={{
                    backgroundColor: disabledViewComplaint
                      ? '#748695f7'
                      : '#007b83',
                    // backgroundColor: '#007b83',
                    color: '#ffffff',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: disabledViewComplaint
                        ? '#748695f7'
                        : '#005f63'
                    }
                  }}
                >
                  {t('dialog.edit')}
                </Button>
                <Button
                  startIcon={<DeleteRoundedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog(complaint);
                  }}
                  sx={{
                    backgroundColor: disabledViewComplaint
                      ? '#748695f7'
                      : '#f44336',
                    // backgroundColor: '#f44336',
                    color: '#ffffff',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: disabledViewComplaint
                        ? '#748695f7'
                        : '#d32f2f'
                    }
                  }}
                >
                  {t('dialog.delete')}
                </Button>
              </CardActions>
            )}
          </Card>
        ))}
      </Box>

      <Box display="flex" justifyContent="center" mt={4}>
        <Pagination
          count={Math.ceil(filterComplaints().length / itemsPerPage)}
          page={page}
          onChange={handlePageChange}
        />
      </Box>

      {viewMode && selectedComplaint && (
        <Modal
          open={viewMode}
          onClose={handleCloseViewDialog}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 'none'
          }}
        >
          <Paper
            elevation={4}
            sx={{
              width: '50%',
              maxWidth: '800px',
              padding: '20px',
              backgroundColor: '#fefefe',
              borderRadius: '12px',
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}
          >
            {/* Header - Title and Close Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  letterSpacing: '0.5px'
                }}
              >
                {t('complaints.complaintDetails')}
              </Typography>
              <Button
                onClick={handleCloseViewDialog}
                sx={{
                  backgroundColor: '#e57373',
                  color: '#fff',
                  borderRadius: '8px',
                  padding: '5px 15px',
                  '&:hover': {
                    backgroundColor: '#d32f2f'
                  }
                }}
              >
                {t('complaints.close')}
              </Button>
            </Box>

            {/* Student Information */}
            <Box
              style={{
                backgroundColor: '#e3f2fd',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #ccc'
              }}
            >
              <Box>
                <Typography variant="body1" style={{ color: '#333' }}>
                  {t('columns.student')}:
                  <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {selectedComplaint.student.name}
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ color: '#333' }}>
                  {t('columns.class')}:
                  <span style={{ color: '#1976d2' }}>
                    {selectedComplaint.student.class}
                  </span>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ color: '#333' }}>
                  {t('columns.professor')}:
                  <span style={{ color: '#1976d2' }}>
                    {selectedComplaint.student.professor}
                  </span>
                </Typography>
              </Box>
            </Box>

            {/* Messages Thread */}
            <Box
              id="messagesContainer"
              sx={{
                maxHeight: '350px',
                overflowY: 'auto',
                padding: '10px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #ddd',
                marginBottom: '20px'
              }}
            >
              {complaintThread.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: '10px',
                    marginBottom: '10px',
                    borderRadius: '8px',
                    backgroundColor:
                      message.poruku_poslao === 'Administrator'
                        ? '#e1f5fe'
                        : '#fafafa',
                    border: '1px solid #ddd'
                  }}
                >
                  <Box
                    style={{
                      backgroundColor:
                        message.poruku_poslao === 'Administrator'
                          ? '#e1f5fe'
                          : '#fafafa',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#1976d2', fontWeight: 'bold' }}
                    >
                      {message.poruku_poslao === 'Administrator'
                        ? t('complaints.administrator')
                        : t('complaints.school')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#777',
                        fontSize: '12px',
                        textAlign: 'right'
                      }}
                    >
                      {new Date(message.date * 1000).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ marginTop: '8px', color: '#333' }}
                  >
                    {decodeHtmlEntities(message.poruka)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* New Message Input */}
            <TextField
              label={t('complaints.yourMessage')}
              multiline
              rows={3}
              variant="outlined"
              fullWidth
              disabled={disabledViewComplaint}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              sx={{
                marginBottom: '20px',
                backgroundColor: disabledViewComplaint ? '#f0f0f0' : '#ffffff',
                borderRadius: '8px',
                boxShadow: 'inset 0px 1px 2px rgba(0, 0, 0, 0.1)'
              }}
            />

            {/* Action Buttons */}
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '20px'
              }}
            >
              <Button
                variant="outlined"
                style={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  padding: '10px 20px',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '8px'
                }}
                onClick={() =>
                  window.open(
                    `/kengur2/#/school/${selectedComplaint.id_skole}`,
                    '_blank'
                  )
                }
              >
                {t('complaints.worksButton')}
              </Button>
              <Button
                variant="contained"
                disabled={disabledViewComplaint}
                style={{
                  // backgroundColor: '#1976d2',
                  backgroundColor: disabledViewComplaint
                    ? '#f0f0f0'
                    : '#1976d2',

                  color: 'white',
                  padding: '10px 20px',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '8px'
                }}
                onClick={handleSendMessage}
              >
                {t('complaints.send')}
              </Button>
            </Box>
          </Paper>
        </Modal>
      )}

      {editMode && selectedComplaint && (
        <Suspense fallback={<div>Loading...</div>}>
          <EditComplaintDialog
            open={editMode}
            onClose={handleCloseEditDialog}
            complaintData={selectedComplaint}
            schoolId={schoolId ? schoolId : 0}
            onSubmit={() => {
              fetchComplaints();
            }}
            roleProp={role}
          />
        </Suspense>
      )}

      {deleteDialogOpen && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={() => handleDelete(selectedComplaint.id)}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={t('dialog.successfullyDeleted')}
      />
    </Box>
  );
};

export default Complaints;
